import { supabase } from '@/shared/libs/supabase';

export type StorageBucket = 'pasted-images' | 'uploaded-files';

export async function uploadFile(
  bucket: StorageBucket,
  userId: string,
  resourceId: string,
  file: File | Blob,
  ext: string
): Promise<{ storageBucket: StorageBucket; storagePath: string }> {
  const safeExt = ext.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  const filename = `${crypto.randomUUID()}.${safeExt}`;
  const storagePath = `${userId}/${resourceId}/${filename}`;

  const contentType = file instanceof File ? file.type : file.type || 'application/octet-stream';

  const { error } = await supabase.storage
    .from(bucket)
    .upload(storagePath, file, { contentType, upsert: false });

  if (error) throw error;

  return { storageBucket: bucket, storagePath };
}

interface CachedSignedUrl {
  url: string;
  expiresAt: number;
  promise: Promise<string> | null;
}
const signedUrlCache = new Map<string, CachedSignedUrl>();

const SIGNED_URL_REFRESH_MARGIN_MS = 60_000;
const MAX_SIGNED_URL_CACHE_ENTRIES = 500;

function pruneSignedUrlCache(now = Date.now()) {
  for (const [key, cached] of signedUrlCache) {
    if (!cached.promise && cached.expiresAt - SIGNED_URL_REFRESH_MARGIN_MS <= now) {
      signedUrlCache.delete(key);
    }
  }

  while (signedUrlCache.size >= MAX_SIGNED_URL_CACHE_ENTRIES) {
    const oldest = signedUrlCache.keys().next();
    if (oldest.done) return;
    signedUrlCache.delete(oldest.value);
  }
}

/**
 * Create a signed URL for a private storage object (default 1 hour).
 */
export async function resolveSignedUrl(
  bucket: StorageBucket,
  path: string,
  expiresIn = 3600,
  options?: { download?: string | boolean }
): Promise<string> {
  const cacheKey = options?.download ? null : `${bucket}:${path}:${expiresIn}`;

  if (cacheKey) {
    const now = Date.now();
    pruneSignedUrlCache(now);
    const cached = signedUrlCache.get(cacheKey);
    if (cached) {
      if (cached.promise) return cached.promise;
      if (cached.expiresAt - SIGNED_URL_REFRESH_MARGIN_MS > now) return cached.url;
    }
  }

  const promise = supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn, options)
    .then(({ data, error }) => {
      if (error || !data?.signedUrl) {
        throw error ?? new Error('Failed to create signed URL');
      }
      return data.signedUrl;
    });

  if (cacheKey) {
    signedUrlCache.set(cacheKey, {
      url: '',
      expiresAt: 0,
      promise,
    });
    promise
      .then((url) => {
        signedUrlCache.set(cacheKey, {
          url,
          expiresAt: Date.now() + expiresIn * 1000,
          promise: null,
        });
      })
      .catch(() => {
        signedUrlCache.delete(cacheKey);
      });
  }

  return promise;
}

export async function deleteFile(bucket: StorageBucket, path: string): Promise<void> {
  const { error } = await supabase.storage.from(bucket).remove([path]);
  if (error) throw error;
}

export function extFromMime(mime: string): string {
  const map: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'text/plain': 'txt',
    'text/csv': 'csv',
  };
  return map[mime] ?? 'bin';
}

/** MIME types accepted by the `uploaded-files` bucket. */
export const UPLOADED_FILES_ACCEPT = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
] as const;

/** Max file size for the `uploaded-files` bucket (25 MB). */
export const UPLOADED_FILES_MAX_SIZE = 25 * 1024 * 1024;

/** Max file size for the `pasted-images` bucket (5 MB). */
export const PASTED_IMAGES_MAX_SIZE = 5 * 1024 * 1024;
